import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, api, type SafeUser } from "../../lib/api";
import { cn } from "../../lib/cn";
import { ConfirmDialog } from "../ConfirmDialog";
import { useToast } from "../toast/ToastProvider";

interface UsersSectionProps {
  currentUser: SafeUser;
}

export function UsersSection({ currentUser }: UsersSectionProps) {
  const qc = useQueryClient();
  const { show } = useToast();
  const [adding, setAdding] = useState(false);
  const [resettingPwFor, setResettingPwFor] = useState<SafeUser | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<SafeUser | null>(null);

  const users = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<SafeUser[]>("/api/v1/users"),
  });

  const setDisabled = useMutation({
    mutationFn: ({ id, disabled }: { id: string; disabled: boolean }) =>
      api.patch(`/api/v1/users/${encodeURIComponent(id)}`, { disabled }),
    onSuccess: (_, args) => {
      qc.invalidateQueries({ queryKey: ["users"] });
      show(args.disabled ? "User disabled" : "User enabled", undefined, "success");
    },
    onError: (err) => show("Update failed", err instanceof Error ? err.message : "Could not update user", "error"),
  });
  const unlock = useMutation({
    mutationFn: (id: string) =>
      api.post(`/api/v1/users/${encodeURIComponent(id)}/unlock`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      show("User unlocked", undefined, "success");
    },
    onError: (err) => show("Unlock failed", err instanceof Error ? err.message : "Could not unlock user", "error"),
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      api.delete(`/api/v1/users/${encodeURIComponent(id)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      show("User removed", undefined, "success");
    },
    onError: (err) => show("Delete failed", err instanceof Error ? err.message : "Could not remove user", "error"),
  });

  return (
    <>
      <section className="card overflow-hidden">
        <div className="flex items-center justify-between gap-4 border-b border-rule px-6 py-4">
          <div>
            <h2 className="font-display text-[18px] font-semibold tracking-tight text-ink">
              Users
            </h2>
            <p className="mt-1 text-[13px] text-ash">
              Admins manage every part of the agent; operators run crawls and
              view results but cannot edit endpoints, manage users, or rotate
              certs.
            </p>
          </div>
          <button
            onClick={() => setAdding(true)}
            className="btn-primary whitespace-nowrap"
          >
            <span className="text-[15px] leading-none">+</span> Add user
          </button>
        </div>

        {users.isLoading ? (
          <p className="px-6 py-8 text-sm text-ash">Loading…</p>
        ) : null}

        {users.data && users.data.length > 0 ? (
          <table className="w-full text-sm">
            <thead className="border-b border-rule bg-paper text-left">
              <tr>
                <Th>Username</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule">
              {users.data.map((u) => {
                const isSelf = u.id === currentUser.id;
                return (
                  <tr key={u.id} className="transition-colors hover:bg-paper">
                    <Td>
                      <div className="flex items-center gap-3">
                        <div className="grid h-8 w-8 place-items-center rounded-full bg-paper font-mono text-[10.5px] font-semibold uppercase text-ink">
                          {u.username.slice(0, 2)}
                        </div>
                        <div>
                          <div className="font-semibold text-ink">
                            {u.username}
                          </div>
                          {isSelf && (
                            <div className="font-mono text-[10px] uppercase tracking-wider text-ash">
                              You
                            </div>
                          )}
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <span
                        className={cn(
                          "chip",
                          u.role === "admin" ? "chip-brand" : "chip-neutral",
                        )}
                      >
                        {u.role}
                      </span>
                    </Td>
                    <Td>
                      {u.disabled ? (
                        <span className="chip-rose">Disabled</span>
                      ) : (
                        <span className="chip-emerald">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{" "}
                          Active
                        </span>
                      )}
                      {u.locked && (
                        <span
                          className="chip-amber ml-2"
                          title={
                            u.lockedUntil
                              ? `Locked until ${new Date(u.lockedUntil).toLocaleTimeString()}`
                              : "Locked"
                          }
                        >
                          Locked
                        </span>
                      )}
                      {u.mustChangePassword && (
                        <span className="ml-2 text-[10.5px] uppercase tracking-wider text-amber-700">
                          must change pw
                        </span>
                      )}
                    </Td>
                    <Td>
                      <span className="text-ash">
                        {new Date(u.createdAt).toLocaleDateString()}
                      </span>
                    </Td>
                    <Td className="text-right">
                      <button
                        onClick={() => setResettingPwFor(u)}
                        className="btn-ghost text-[12px]"
                      >
                        Reset pw
                      </button>
                      <button
                        onClick={() => unlock.mutate(u.id)}
                        disabled={!u.locked || unlock.isPending}
                        className="btn-ghost text-[12px]"
                        title={
                          u.locked
                            ? "Clear failed-attempt lockout"
                            : "Account is not locked"
                        }
                      >
                        Unlock
                      </button>
                      <button
                        onClick={() =>
                          setDisabled.mutate({
                            id: u.id,
                            disabled: !u.disabled,
                          })
                        }
                        disabled={isSelf || setDisabled.isPending}
                        className="btn-ghost text-[12px]"
                        title={isSelf ? "You can't disable yourself" : ""}
                      >
                        {u.disabled ? "Enable" : "Disable"}
                      </button>
                      <button
                        onClick={() => setConfirmDelete(u)}
                        disabled={isSelf}
                        className="btn-danger text-[12px]"
                        title={isSelf ? "You can't delete yourself" : ""}
                      >
                        Delete
                      </button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : null}

        {setDisabled.error instanceof ApiError && (
          <p className="px-6 py-2 text-[12px] text-rose-700">
            {setDisabled.error.message}
          </p>
        )}
        {remove.error instanceof ApiError && (
          <p className="px-6 py-2 text-[12px] text-rose-700">
            {remove.error.message}
          </p>
        )}
      </section>

      <AddUserDialog open={adding} onOpenChange={setAdding} />
      <ResetPasswordDialog
        target={resettingPwFor}
        onClose={() => setResettingPwFor(null)}
      />
      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete user"
        description={`Delete user "${confirmDelete?.username}"? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={() => {
          if (confirmDelete) remove.mutate(confirmDelete.id);
          setConfirmDelete(null);
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </>
  );
}

function AddUserDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const qc = useQueryClient();
  const { show } = useToast();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<"admin" | "operator">("operator");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.post("/api/v1/users", { username, password, role }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setUsername("");
      setPassword("");
      setRole("operator");
      setError(null);
      onOpenChange(false);
      show("User created", `${username} added successfully`, "success");
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : "Create failed";
      setError(msg);
      show("Create failed", msg, "error");
    },
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-ink/40 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-soft border border-rule bg-surface p-6 shadow-cardLift">
          <Dialog.Title asChild>
            <h2 className="font-display text-[20px] font-semibold tracking-tight text-ink">
              Add user
            </h2>
          </Dialog.Title>
          <Dialog.Description className="mt-1.5 mb-5 text-[13px] text-ash">
            New users must change their password on first login.
          </Dialog.Description>
          <div className="space-y-4">
            <Field label="Username">
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="off"
                className="input"
              />
            </Field>
            <Field label="Initial password">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                className="input font-mono"
                placeholder="≥ 12 chars; mixed case + digit + symbol"
              />
            </Field>
            <Field label="Role">
              <select
                value={role}
                onChange={(e) =>
                  setRole(e.target.value as "admin" | "operator")
                }
                className="input"
              >
                <option value="operator">
                  Operator — runs crawls, views results
                </option>
                <option value="admin">Admin — full control</option>
              </select>
            </Field>
            {error && (
              <div className="rounded-md border border-rose-500/30 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2 border-t border-rule pt-4">
              <button
                onClick={() => onOpenChange(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => create.mutate()}
                disabled={!username.trim() || !password || create.isPending}
                className="btn-primary"
              >
                {create.isPending ? "Creating…" : "Create user"}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ResetPasswordDialog({
  target,
  onClose,
}: {
  target: SafeUser | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { show } = useToast();
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reset = useMutation({
    mutationFn: () =>
      api.patch(`/api/v1/users/${encodeURIComponent(target!.id)}`, {
        newPassword,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setNewPassword("");
      setError(null);
      onClose();
      show("Password reset", "Password updated successfully", "success");
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : "Reset failed";
      setError(msg);
      show("Reset failed", msg, "error");
    },
  });

  return (
    <Dialog.Root
      open={target !== null}
      onOpenChange={(o) => {
        if (!o) {
          setNewPassword("");
          setError(null);
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-ink/40 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-soft border border-rule bg-surface p-6 shadow-cardLift">
          <Dialog.Title asChild>
            <h2 className="font-display text-[20px] font-semibold tracking-tight text-ink">
              Reset password
            </h2>
          </Dialog.Title>
          <Dialog.Description className="mt-1.5 mb-5 text-[13px] text-ash">
            {target
              ? `Set a new password for ${target.username}. They'll be required to change it on next login.`
              : ""}
          </Dialog.Description>
          <div className="space-y-4">
            <Field label="New password">
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                className="input font-mono"
              />
            </Field>
            {error && (
              <div className="rounded-md border border-rose-500/30 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2 border-t border-rule pt-4">
              <button
                onClick={() => {
                  setNewPassword("");
                  setError(null);
                  onClose();
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => reset.mutate()}
                disabled={!newPassword || reset.isPending}
                className="btn-primary"
              >
                {reset.isPending ? "Saving…" : "Set password"}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
        {label}
      </span>
      {children}
    </label>
  );
}

function Th({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={
        "px-5 py-3 font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-ash " +
        (className ?? "")
      }
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <td className={"px-5 py-4 align-top " + (className ?? "")}>{children}</td>
  );
}
